import { createContext, useContext, ReactNode, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { featureFlagsQueryOptions } from "../api/featureFlags";
import { useAuth } from "../auth/authProvider";

interface FeatureFlagsContextType {
    flags: Map<string, boolean>;
    isEnabled: (featureName: string) => boolean;
    isNew: (featureName: string) => boolean;
    isLoading: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface FeatureFlagsProviderProps {
    children: ReactNode;
}

export const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
    const { isAuthenticated } = useAuth();

    const { data: enabledFeatures = [], isLoading } = useQuery({
        ...featureFlagsQueryOptions,
        enabled: isAuthenticated,
    });

    const enabledFeaturesMap = useMemo(() => {
        if (!enabledFeatures) {
            return new Map<string, boolean>();
        }
        return new Map(enabledFeatures.map((flag) => [flag.featureName.toLowerCase(), flag.isNew]));
    }, [enabledFeatures]);

    const isEnabled = (featureName: string): boolean => {
        return enabledFeaturesMap.has(featureName.toLowerCase());
    };

    const isNew = (featureName: string): boolean => {
        return enabledFeaturesMap.get(featureName.toLowerCase()) || false;
    };

    return (
        <FeatureFlagsContext.Provider value={{ flags: enabledFeaturesMap, isEnabled, isNew, isLoading }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
};

export const useFeatureFlags = () => {
    const context = useContext(FeatureFlagsContext);
    if (!context) {
        throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
    }
    return context;
};
